import styled from "@emotion/styled";
import { type ReactNode } from "react";

import {
  Button,
  FONT_WEIGHT_SEMI_BOLD,
  FormRow,
  Icon,
  stopClickPropagationProps,
  Tooltip,
  useResizeObserver,
  useViewBarSettings,
  withDataTest,
  zIndexes,
} from "@skilitics/designer-ui-components";
import type { FormatMessages, FormatStrings } from "@skilitics/localization";
import { useFormatMessage } from "@skilitics/localization";
import type { NodeRole } from "@skilitics/nautilus-core";

import { NodeDesigner } from "../NodeDesigner";
import { NodeSelectionWrapper } from "../selection/NodeSelectionWrapper.tsx";
import { useIsNodeSelected } from "../selection/useSelectedNode.ts";
import { useSlotDesigner } from "./useSlotDesigner";

const Container = styled.div`
  position: relative;
`;

const Card = styled.div`
  min-width: 208px;
  width: fit-content;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  color: ${({ theme }) => theme.generalTextIcon};
  background-color: ${({ theme }) => theme.surface};
  user-select: none;
  box-shadow: ${({ theme }) => theme.boxShadow.normal};
`;

const SlotDesignerContainer = styled.div``;

export const ResidentToolbarContainer = styled.div`
  position: absolute;
  top: -40px;

  width: fit-content;

  opacity: 1;

  transition:
    opacity 150ms ease-in-out,
    display 150ms ease-in-out allow-discrete;

  z-index: ${zIndexes.activitySlotToolbar};

  @starting-style {
    opacity: 0;
  }
`;

const PlaceholderContainer = styled.div`
  form {
    margin: 0;
  }

  ${FormRow} {
    padding: 0;
    margin: 0;
  }
`;

const ResidentSummaryContainer = styled.div`
  position: relative;
  height: 100%;
  flex: 1 1 100%;

  padding: 4px;

  & button:hover {
    border: 0;
  }
`;

const StyledChooseResidentButton = styled(Button)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  width: 100%;
  height: 140px;

  border-radius: ${({ theme }) => theme.borderRadius}px;
  color: ${({ theme }) => theme.generalTextIcon};
  background: ${({ theme }) => theme.surfaceSoft};
  line-height: 1.5em;

  border: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;

  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.primary};
    opacity: 0;
    transition: opacity 0.1s ease-in-out;
    pointer-events: none;
  }
  &:hover {
    &:after {
      opacity: 0.2;
    }
  }
`;

const ChooseResidentButton = withDataTest(StyledChooseResidentButton, "choose-resident-button");

const Title = styled.span``;

const SummaryBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;

  height: 24px;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const SummaryTimer = styled.div`
  flex: 1 1 20%;
`;

const SummaryResidents = styled.div`
  flex: 1 1 60%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
`;

const SummaryAttempts = styled.span`
  flex: 1 1 20%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: right;
  gap: 2px;

  color: ${({ theme }) => theme.primaryStrong};
`;

const Separator = styled.div`
  width: 1px;
  height: 12px;
  margin: 0 4px;

  background-color: ${({ theme }) => theme.outlineSoft};
`;

const Circle = styled.span`
  height: 8px;
  width: 8px;

  border-radius: 50%;
  background: ${({ theme }) => theme.generalTextIconVariant};
`;

const NavGroupSummaryContainer = styled.div`
  cursor: pointer;
  background: ${({ theme }) => theme.surfaceSoft};
  margin: 5px;
  border-radius: ${({ theme }) => theme.borderRadius}px;
`;

const TitleContainer = styled.div`
  background: ${({ theme }) => theme.surfaceVariant};
  border-radius: ${({ theme }) => `${theme.borderRadius}px ${theme.borderRadius}px 0 0`};

  .MuiInputBase-root {
    font-size: ${({ theme }) => theme.fontSize};
    font-weight: ${FONT_WEIGHT_SEMI_BOLD};
    padding: 0;
    height: 44px;
    padding: 5px 0;
    &:hover {
      background: inherit;
    }
  }

  textarea {
    text-align: center;
    padding: 0 10px;
  }

  // Limit the title to 2 lines when not focused, allowing it to expand when editing
  textarea:not(:focus) {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    max-height: 2lh;
    overflow: hidden;
  }
`;

const ObjectiveContainer = styled.div`
  .MuiInputBase-root {
    font-size: ${({ theme }) => theme.fontSize};
    padding: 8px 0;
  }

  textarea {
    padding: 0 12px;
  }

  // Limit the title to 2 lines when not focused, allowing it to expand when editing
  textarea:not(:focus) {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    max-height: 2lh;
    overflow: hidden;
  }
`;

export type ActivitySlotDesignerProps = {
  slotId: { parentId: number; name: string };
  role?: keyof typeof roleTitleFormatStrings;
  maxAttempts: number;
  onOpen: () => void;
  onBrowse: () => void;
  titleEditor: ReactNode;
  interludeCount: number;
};

// TODO: rename and make role not default
export const ActivitySlotDesigner = ({
  maxAttempts,
  onOpen,
  onBrowse,
  role = "activity",
  slotId,
  titleEditor,
  interludeCount,
}: ActivitySlotDesignerProps) => {
  const { showGoals } = useViewBarSettings();
  const { placeholder, resident, removeSlotResident } = useSlotDesigner(slotId);
  const formatMessage = useFormatMessage(formatStrings);
  const formatRoleTitle = useFormatMessage(roleTitleFormatStrings);
  const roleTitle = formatRoleTitle(role);

  const { ref: cardRef, width: cardWidth = 0 } = useResizeObserver<HTMLDivElement>();
  const { ref: toolbarRef, width: toolbarWidth = 0 } = useResizeObserver<HTMLDivElement>();

  const isContentSelected = useIsNodeSelected(resident?.id ?? placeholder?.id ?? -1);

  if (!roleTitle) {
    throw new Error(`Format string not defined for role ${role}`);
  }

  const handleClick = () => {
    if (resident) return;
    onBrowse();
  };

  const handleDoubleClick = () => {
    if (!resident) return;
    onOpen();
  };

  const handleBrowseOrChangeResidentClick = () => {
    onBrowse();
  };

  const isGroup = resident?.role === "activity-group";

  const showContentToolbar = isContentSelected && !!resident;

  return (
    <Container onDoubleClick={(e) => e.stopPropagation()}>
      <NodeSelectionWrapper nodeId={slotId.parentId}>
        <Card ref={cardRef}>
          <SlotDesignerContainer data-test="slot-designer-container">
            {placeholder && (
              <PlaceholderContainer>
                <TitleContainer>{titleEditor}</TitleContainer>
                {showGoals && (
                  <ObjectiveContainer>
                    <NodeDesigner node={placeholder} view="edit" />
                  </ObjectiveContainer>
                )}

                <ResidentSummaryContainer>
                  {showContentToolbar && resident && (
                    <ResidentToolbarContainer
                      ref={toolbarRef}
                      style={{ left: `${(cardWidth - toolbarWidth) / 2}px` }}
                      {...stopClickPropagationProps}
                    >
                      <NodeDesigner node={resident} view="toolbar" onRemove={removeSlotResident} />
                    </ResidentToolbarContainer>
                  )}

                  {resident ? (
                    <Tooltip title={resident.title}>
                      {resident.role === "activity-group" ? (
                        <NavGroupSummaryContainer
                          onClick={handleClick}
                          onDoubleClick={handleDoubleClick}
                          data-test="nav-group-container"
                        >
                          <NodeDesigner node={resident} view="summary" ownsProperties />
                        </NavGroupSummaryContainer>
                      ) : (
                        <NodeSelectionWrapper nodeId={resident.id}>
                          <ChooseResidentButton
                            variant="outlined"
                            onClick={handleClick}
                            onDoubleClick={handleDoubleClick}
                            data-test="resident-button"
                          >
                            <NodeDesigner node={resident} view="summary" ownsProperties />
                          </ChooseResidentButton>
                        </NodeSelectionWrapper>
                      )}
                    </Tooltip>
                  ) : (
                    <ChooseResidentButton variant="outlined" onClick={handleBrowseOrChangeResidentClick}>
                      {placeholder && <NodeDesigner node={placeholder} view="summary" ownsProperties />}
                      <Title>{formatMessage("chooseResident", { roleTitle })}</Title>
                    </ChooseResidentButton>
                  )}

                  {!isGroup && (
                    <SummaryBar>
                      <SummaryTimer>{/* placeholder for where timer will go. */}</SummaryTimer>
                      <SummaryResidents>
                        {!!resident && <Circle />}
                        {!!resident && !!interludeCount && <Separator />}
                        {!!interludeCount &&
                          Array.from({ length: interludeCount }).map((_, index) => <Circle key={index} />)}
                      </SummaryResidents>
                      <SummaryAttempts>
                        {!!maxAttempts && (
                          <>
                            {maxAttempts}
                            <Icon icon="arrow-rotate-left" size={12} />
                          </>
                        )}
                      </SummaryAttempts>
                    </SummaryBar>
                  )}
                </ResidentSummaryContainer>
              </PlaceholderContainer>
            )}
          </SlotDesignerContainer>
        </Card>
      </NodeSelectionWrapper>
    </Container>
  );
};

const formatStrings = {
  chooseResident: {
    en: "Choose {roleTitle}",
  },
} satisfies FormatMessages;

const roleTitleFormatStrings = {
  activity: {
    en: "Content",
  },
} satisfies Partial<Record<NodeRole, FormatStrings>>;
